import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HTTP_TIMEOUT_MS } from '../../../config';
import { Tabs, LoadingSpinner, useToastContext, useDirtyForm, ConfirmationModal, PageHeader } from '@airclou/ui';
import type { Tab } from '@airclou/ui';
import { useRoutedTabs } from '@/hooks/useRoutedTabs';
import { useAuth } from '@/contexts/AuthContext';
import { ErrorState } from '@/components/state/ErrorState';
import { authServiceFetch } from '@/services/authServiceFetch';

import { GeneralTab, looksLikeMetadataXml } from './GeneralTab.tsx';
import type { SsoFormValues, IdentityProvider } from './GeneralTab.tsx';
import { DomainsTab } from './DomainsTab.tsx';
import { AuditTab } from './AuditTab.tsx';

interface SSOConfig {
  sso_enabled: boolean;
  idp: IdpConfig | null;
  domains: DomainConfig[];
}

interface IdpConfig {
  id: string;
  provider?: 'azure_ad' | 'google' | 'okta' | 'other';
  saml_entry_point: string;
  saml_metadata_url?: string;
  saml_issuer?: string;
  saml_cert: string;
  saml_logout_url?: string;
  saml_want_assertions_signed: boolean;
  saml_disable_requested_authn_context: boolean;
}

interface DomainConfig {
  id: string;
  domain: string;
  verified: boolean;
  created_at: string;
}

interface AuditLog {
  id: string;
  event_type: string;
  user_id?: string;
  ip_address?: string;
  created_at: string;
}

const AUTH_API_BASE = import.meta.env.VITE_AUTH_SERVICE_URL || 'https://auth.airclou.com';

const EMPTY_SSO_FORM: SsoFormValues = {
  ssoEnabled: false,
  identityProvider: 'Azure AD',
  idpSsoUrl: '',
  idpMetadataUrl: '',
  idpMetadata: '',
  enforceSSO: false,
};

export function SSOManagement() {
  const { t } = useTranslation('dashboard');
  const { success, error } = useToastContext();
  const [config, setConfig] = useState<SSOConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Form state. The baseline is whatever the Auth service last returned, so
  // Save stays disabled until something actually differs from it — this page
  // used to re-PUT the config (and re-fetch IdP metadata) on every click.
  const form = useDirtyForm<SsoFormValues>(EMPTY_SSO_FORM);
  const { values, setField, baseline, isDirty } = form;

  // Turning enforcement on locks every member of the tenant out of password
  // login, this admin included. It is the one change on this page that cannot
  // be undone from inside the app if the IdP is misconfigured, so it gets a
  // deliberate confirmation rather than committing from a stray toggle.
  const [enforceConfirmOpen, setEnforceConfirmOpen] = useState(false);

  const [newDomain, setNewDomain] = useState('');
  // Kept after the modal closes, so its text doesn't blank out while it fades.
  const [domainToRemove, setDomainToRemove] = useState<{ domain: string; verified: boolean } | null>(null);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [removingDomain, setRemovingDomain] = useState(false);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [auditLogsError, setAuditLogsError] = useState(false);
  // Starts true: the fetch is kicked off from an effect, so a `false` here paints
  // an empty audit table for one frame before the request even leaves.
  const [auditLogsLoading, setAuditLogsLoading] = useState(true);

  // Which tenant this page is showing. An org switch changes `tenantId` under the
  // same mounted page, and neither the config nor the audit log of the tenant we
  // left may render under the new one — here that is not just a stale row but
  // another organization's data. Written from the effect, not during render.
  const shownTenantId = useRef('');

  // And which load is the current one, per region. The tenant id alone does not
  // order two loads for the *same* tenant, which is the likely case here: a save
  // reloads the config, and the retry can fire another. Letting an earlier answer
  // land last would re-baseline the form from a row that predates the save.
  const configSeq = useRef(0);
  const auditSeq = useRef(0);

  // Get tenant_id from current user context
  const { user, token } = useAuth();
  const tenantId = user?.tenantId || '';

  // Every call here goes to the Auth service, which accepts an explicit Bearer
  // header or the ambient airclou_session cookie. Send the header: it names the
  // identity this page is actually displaying. Cookie-only requests are answered
  // for whoever signed in last anywhere on .airclou.com, which is how this page
  // once reported "admin privileges required" to a signed-in admin.
  // See apps/auth/docs/session-credential-standardization-plan.md
  const authHeaders = (extra?: Record<string, string>): Record<string, string> => ({
    ...(extra || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  });

  // Static ACS URL (same for all tenants - tenant context passed via RelayState)
  const acsUrl = `${AUTH_API_BASE}/saml/acs`;

  // Use the routing hook for tab management. The query string is the audit
  // tab's sort, search and page, and nothing else reads it, so it rides along
  // to the other tabs: an admin who sorts the log, checks Domains and comes
  // back finds the sort as they left it.
  const { activeTab, handleTabChange } = useRoutedTabs({
    basePath: '/sso',
    validTabs: ['general', 'domains', 'audit'],
    defaultTab: 'general',
    keepSearch: true,
  });

  const loadSSOConfig = async () => {
    const loadTenantId = tenantId;
    const seq = ++configSeq.current;
    const isCurrent = () => seq === configSeq.current && loadTenantId === shownTenantId.current;
    try {
      const response = await authServiceFetch(`${AUTH_API_BASE}/sso/admin/config?tenant_id=${tenantId}`, {
        headers: authHeaders(),
        credentials: 'include',
        signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
      });
      const data = await response.json();
      if (!isCurrent()) return;

      if (data.success) {
        setConfig(data.data);
        setLoadError(false);
        setLoadErrorMessage(null);

        const providerMap: Record<string, IdentityProvider> = {
          'azure_ad': 'Azure AD',
          'google': 'Google',
          'okta': 'Okta',
          'other': 'Other',
        };
        const idp = data.data.idp;

        // Adopt what the service holds as the baseline, so the save bar only
        // appears once the admin changes something.
        form.setBaseline({
          ssoEnabled: !!data.data.sso_enabled,
          enforceSSO: !!data.data.enforce_sso,
          identityProvider: idp?.provider
            ? providerMap[idp.provider] || 'Other'
            : EMPTY_SSO_FORM.identityProvider,
          idpSsoUrl: idp?.saml_entry_point || '',
          idpMetadataUrl: idp?.saml_metadata_url || '',
          idpMetadata: idp?.saml_cert || '',
        });
      } else {
        setLoadError(true);
        setLoadErrorMessage(data.error?.message || null);
      }
    } catch (err) {
      if (!isCurrent()) return;
      setLoadError(true);
      setLoadErrorMessage(null);
      console.error('Failed to load SSO config:', err);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const loadAuditLogs = async () => {
    const loadTenantId = tenantId;
    const seq = ++auditSeq.current;
    const isCurrent = () => seq === auditSeq.current && loadTenantId === shownTenantId.current;
    try {
      setAuditLogsLoading(true);
      const response = await authServiceFetch(`${AUTH_API_BASE}/sso/admin/audit-logs?tenant_id=${tenantId}&limit=50`, {
        headers: authHeaders(),
        credentials: 'include',
        signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
      });
      const data = await response.json();
      if (!isCurrent()) return;

      if (data.success) {
        setAuditLogs(data.data);
        setAuditLogsError(false);
      } else {
        setAuditLogsError(true);
        console.error('Failed to load audit logs:', data.error);
      }
    } catch (err) {
      if (!isCurrent()) return;
      setAuditLogsError(true);
      console.error('Failed to load audit logs:', err);
    } finally {
      if (isCurrent()) setAuditLogsLoading(false);
    }
  };

  useEffect(() => {
    shownTenantId.current = tenantId;
    if (!tenantId) return;
    // A different organization under the same mounted page: drop the previous
    // tenant's config and audit rows before loading the new one, and put both
    // regions back into their loading state.
    setConfig(null);
    form.setBaseline(EMPTY_SSO_FORM);
    setAuditLogs([]);
    setAuditLogsError(false);
    setLoadError(false);
    setLoadErrorMessage(null);
    setLoading(true);
    setAuditLogsLoading(true);
    loadSSOConfig();
    loadAuditLogs();
  }, [tenantId]);

  // A refusal the domain route names by code, so it reads in the user's language
  // rather than the server's English. Anything else keeps the server's message.
  const domainRefusal = (code: string | undefined): string | undefined => {
    if (code === 'SHARED_EMAIL_DOMAIN') return t('sso.sharedEmailDomain');
    if (code === 'DOMAIN_NOT_CLAIMED') return t('sso.domainNotClaimed');
    return undefined;
  };

  const addDomain = async () => {
    if (!newDomain) {
      error(t('sso.pleaseEnterDomain'));
      return;
    }
    
    try {
      const response = await authServiceFetch(`${AUTH_API_BASE}/sso/admin/domains?tenant_id=${tenantId}`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
        body: JSON.stringify({ domain: newDomain }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        success(t('sso.domainAdded'));
        setNewDomain('');
        loadSSOConfig();
      } else {
        error(domainRefusal(data.error?.code) || data.error?.message || t('sso.failedToAddDomain'));
      }
    } catch (err) {
      error(t('sso.failedToAddDomain'));
      console.error(err);
    }
  };

  const removeDomain = async () => {
    if (!domainToRemove) return;
    const { domain } = domainToRemove;
    setRemovingDomain(true);
    try {
      const response = await authServiceFetch(`${AUTH_API_BASE}/sso/admin/domains/${encodeURIComponent(domain)}?tenant_id=${tenantId}`, {
        method: 'DELETE',
        headers: authHeaders(),
        credentials: 'include',
        signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
      });
      
      const data = await response.json();
      
      if (data.success) {
        success(t('sso.domainRemoved'));
        loadSSOConfig();
      } else {
        error(data.error?.message || t('sso.failedToRemoveDomain'));
      }
    } catch (err) {
      error(t('sso.failedToRemoveDomain'));
      console.error(err);
    } finally {
      setRemovingDomain(false);
      setRemoveConfirmOpen(false);
    }
  };

  // Why Save is blocked, or null when the form can be committed. Turning SSO on
  // without the IdP details it needs produces a tenant that cannot sign in, so
  // the required fields are checked here rather than discovered at login.
  const validationMessage = useMemo<string | null>(() => {
    if (!values.ssoEnabled) return null;
    if (values.identityProvider === 'Azure AD') {
      return values.idpMetadataUrl.trim() ? null : t('sso.metadataUrlRequired');
    }
    const metadata = values.idpMetadata.trim();
    if (!metadata) return t('sso.metadataRequired');
    // A sign-in address is not optional — without one there is nowhere to send
    // the user, which is how this page used to store a config that could never
    // work. It can come from a pasted metadata document or from the field; one
    // of the two has to be there.
    if (!looksLikeMetadataXml(metadata) && !values.idpSsoUrl.trim()) {
      return t('sso.ssoLoginUrlRequired');
    }
    return null;
  }, [values, t]);

  // Enforcement only bites once SSO itself is on, so compare the effective
  // state. Enabling SSO on a tenant that already had enforcement staged turns
  // enforcement on too, and must confirm just the same.
  const willEnforce = values.enforceSSO && values.ssoEnabled;
  const wasEnforcing = baseline.enforceSSO && baseline.ssoEnabled;

  const requestSave = () => {
    if (validationMessage) return;
    if (willEnforce && !wasEnforcing) {
      setEnforceConfirmOpen(true);
      return;
    }
    saveSSOConfig();
  };

  const saveSSOConfig = async () => {
    setSaving(true);
    const { ssoEnabled, enforceSSO, identityProvider, idpSsoUrl, idpMetadataUrl, idpMetadata } = values;
    try {
      // The IdP fields stay editable while SSO is off, so persist whatever the
      // admin entered even then — otherwise Save reports success and discards it.
      const hasIdpInput = Boolean(idpSsoUrl || idpMetadataUrl || idpMetadata);

      const writeIdp = async () => {
        if (!ssoEnabled && !hasIdpInput) return;
        const payload: any = {
          provider: identityProvider.toLowerCase().replace(/ /g, '_'),
          saml_want_assertions_signed: true,
          saml_disable_requested_authn_context: true,
        };

        if (identityProvider === 'Azure AD') {
          if (idpSsoUrl) {
            payload.saml_entry_point = idpSsoUrl;
          }
          
          if (idpMetadataUrl) {
            try {
              const metadataResponse = await authServiceFetch(`${AUTH_API_BASE}/sso/admin/fetch-metadata`, {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                credentials: 'include',
                signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
                body: JSON.stringify({
                  metadata_url: idpMetadataUrl,
                  tenant_id: tenantId,
                }),
              });

              const metadataData = await metadataResponse.json();

              if (metadataData.success) {
                if (metadataData.data.certificate) payload.saml_cert = metadataData.data.certificate;
                if (metadataData.data.slo_endpoint) payload.saml_logout_url = metadataData.data.slo_endpoint;
                if (metadataData.data.sso_endpoint && !idpSsoUrl) {
                  payload.saml_entry_point = metadataData.data.sso_endpoint;
                }
                payload.saml_metadata_url = idpMetadataUrl;
              } else {
                throw new Error(metadataData.error?.message || 'Failed to fetch metadata');
              }
            } catch (metadataError: any) {
              throw new Error(`Failed to fetch metadata: ${metadataError.message}`);
            }
          }
        } else {
          // A sign-in URL the admin typed is stored whether or not a
          // certificate came with it. It used to be written only from inside
          // the metadata branch below, so filling in just this field and
          // saving reported success and dropped the value — the field then
          // came back empty on the reload, which is the exact failure
          // `hasIdpInput` exists to prevent.
          if (idpSsoUrl) payload.saml_entry_point = idpSsoUrl;

          // Google, Okta and the rest hand out a metadata document rather than
          // a URL. This used to file whatever was pasted under `saml_cert` and
          // never set `saml_entry_point`, so a non-Azure tenant could be saved
          // into a state SAML login can't use. Read the document instead, the
          // same way the Azure path reads the one it fetches.
          if (idpMetadata && looksLikeMetadataXml(idpMetadata)) {
            const parseResponse = await authServiceFetch(`${AUTH_API_BASE}/sso/admin/parse-metadata?tenant_id=${tenantId}`, {
              method: 'POST',
              headers: authHeaders({ 'Content-Type': 'application/json' }),
              credentials: 'include',
              signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
              body: JSON.stringify({ metadata_xml: idpMetadata }),
            });

            const parseData = await parseResponse.json();
            if (!parseData.success) {
              throw new Error(parseData.error?.message || t('sso.failedToReadMetadata'));
            }

            payload.saml_cert = parseData.data.certificate;
            if (parseData.data.slo_endpoint) payload.saml_logout_url = parseData.data.slo_endpoint;
            // The document's `entity_id` is deliberately NOT stored. It is the
            // IdP's entity ID, while `saml_issuer` is *our* entity ID — the SP
            // one we put in the AuthnRequest and the IdP matches against the
            // audience it has registered (see saml/config.ts, `issuer: // SP
            // Entity ID`). Writing the IdP's id there makes every sign-in
            // request arrive claiming to be from the IdP itself, and the IdP
            // refuses it. The Azure path reads the same field out of its
            // metadata and ignores it for this reason.
            //
            // A URL the admin typed is the deliberate one; the document's is the
            // fallback. Mirrors the Azure branch.
            const entryPoint = idpSsoUrl || parseData.data.sso_endpoint;
            if (!entryPoint) {
              throw new Error(t('sso.noSsoUrlInMetadata'));
            }
            payload.saml_entry_point = entryPoint;
          } else if (idpMetadata) {
            // Not a document — treat the paste as the signing certificate on
            // its own. With SSO on, validation has already required the sign-in
            // URL, which is set above, so what gets stored can be signed in
            // through. With SSO off it can still land without one; that is the
            // "keep what the admin typed" path, and enabling SSO later is gated
            // on the same validation.
            payload.saml_cert = idpMetadata;
          }
        }

        const idpResponse = await authServiceFetch(`${AUTH_API_BASE}/sso/admin/idp?tenant_id=${tenantId}`, {
          method: 'PUT',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          credentials: 'include',
          signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
          body: JSON.stringify(payload),
        });
        
        const idpData = await idpResponse.json();
        if (!idpData.success) {
          throw new Error(idpData.error?.message || 'Failed to update IdP configuration');
        }
      };

      const writeEnabledState = async () => {
        const enableResponse = await authServiceFetch(`${AUTH_API_BASE}/sso/admin/config?tenant_id=${tenantId}`, {
          method: 'PUT',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          credentials: 'include',
          signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
          body: JSON.stringify({ enabled: ssoEnabled, enforce_sso: enforceSSO && ssoEnabled }),
        });

        const enableData = await enableResponse.json();
        if (!enableData.success) {
          throw new Error(enableData.error?.message || 'Failed to update SSO status');
        }
      };

      // These two writes are not atomic, so the order decides which way a
      // half-finished save fails — and it has to flip with the direction.
      //
      // Turning SSO ON: the IdP goes first. Reading the metadata can fail (the
      // document is unreachable, or carries no sign-in URL), and with the
      // enable call first that left SSO switched on for the whole tenant with
      // nothing behind it — with enforcement on, that locks everyone out.
      //
      // Turning SSO OFF: the switch goes first. An admin disabling SSO is often
      // doing it *because* the IdP is unreachable, and making them wait on a
      // metadata fetch from that same IdP would deny them the escape hatch.
      let idpWriteFailed = false;
      if (ssoEnabled) {
        await writeIdp();
        await writeEnabledState();
      } else {
        await writeEnabledState();
        // The switch is stored by this point, so a failure here is not a failed
        // save — SSO is off either way, which is the part the admin came for.
        // Throwing would report the whole save as failed and leave the form
        // dirty against a state the server has already taken, hiding the very
        // escape hatch this ordering exists to provide.
        try {
          await writeIdp();
        } catch (idpError) {
          idpWriteFailed = true;
          console.error(idpError);
        }
      }

      // Awaited so `saving` stays true until the new baseline is in. Firing it
      // and returning let the save bar reappear for a beat, reporting unsaved
      // changes that were in fact already stored. It reports its own failures
      // rather than throwing, so a reload hiccup cannot turn a save that
      // succeeded into a reported failure.
      await loadSSOConfig();
      if (idpWriteFailed) {
        error(t('sso.disabledButIdpNotSaved'));
      } else {
        success(t('sso.configSaved'));
      }
    } catch (err: any) {
      error(err.message || t('sso.failedToSaveConfig'));
      console.error(err);
    } finally {
      setSaving(false);
      // Closed here rather than on click, so the confirmation's own button
      // carries the spinner for the write the operator just authorised.
      setEnforceConfirmOpen(false);
    }
  };

  const tabs: Tab[] = [
    {
      id: 'general',
      label: t('sso.general'),
      // The config is this tab's data, so its loading and failure states live
      // here rather than over the page: a config that would not load must not
      // take the approved-domains and audit tabs — which fetch separately — down
      // with it.
      content: loading ? (
        <div className="flex min-h-[300px] items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : loadError ? (
        <ErrorState
          title={t('sso.failedToLoadConfig')}
          description={loadErrorMessage || t('sso.couldNotLoadSettings')}
          onRetry={() => {
            setLoadError(false);
            setLoadErrorMessage(null);
            // The retry swaps this error state for the spinner branch above, which
            // is the progress affordance — so no `isRetrying` here.
            setLoading(true);
            loadSSOConfig();
          }}
        />
      ) : (
        <GeneralTab
          values={values}
          setField={setField}
          acsUrl={acsUrl}
          saving={saving}
          isDirty={isDirty}
          validationMessage={validationMessage}
          onSave={requestSave}
          onDiscard={form.reset}
        />
      )
    },
    {
      id: 'domains',
      label: t('sso.approvedDomains'),
      content: (
        <DomainsTab
          domains={config?.domains}
          isLoading={loading}
          newDomain={newDomain}
          setNewDomain={setNewDomain}
          onAddDomain={addDomain}
          onRemoveDomain={(d) => {
            setDomainToRemove(d);
            setRemoveConfirmOpen(true);
          }}
        />
      )
    },
    {
      id: 'audit',
      label: t('sso.auditLogs'),
      content: (
        <AuditTab
          auditLogs={auditLogs}
          hasError={auditLogsError}
          isLoading={auditLogsLoading}
          onRetry={loadAuditLogs}
        />
      )
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader title={t('sso.management')} description={t('sso.subtitle')} />

      {/* The strip is part of the shell, so it renders immediately and each tab
          gates only its own data region.
          The cap goes on the tab content, never on the Tabs wrapper: the tab strip
          is page furniture and lines up with the heading above it. The audit tab
          drops the cap for its DataTable. */}
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        contentClassName={activeTab === 'audit' ? undefined : 'max-w-2xl'}
      />

      <ConfirmationModal
        isOpen={enforceConfirmOpen}
        variant="danger"
        title={t('sso.confirmEnforceTitle')}
        description={t('sso.confirmEnforceDescription')}
        confirmText={t('sso.confirmEnforceAction')}
        cancelText={t('common.cancel')}
        isLoading={saving}
        onConfirm={saveSSOConfig}
        onClose={() => setEnforceConfirmOpen(false)}
      />

      <ConfirmationModal
        isOpen={removeConfirmOpen}
        variant="danger"
        title={t('sso.confirmRemoveDomainTitle', { domain: domainToRemove?.domain ?? '' })}
        description={t(
          domainToRemove?.verified ? 'sso.confirmRemoveDomainDescription' : 'sso.confirmRemovePendingDomainDescription',
          { domain: domainToRemove?.domain ?? '' },
        )}
        confirmText={t('common.remove')}
        cancelText={t('common.cancel')}
        isLoading={removingDomain}
        onConfirm={removeDomain}
        onClose={() => { if (!removingDomain) setRemoveConfirmOpen(false); }}
      />
    </div>
  );
}
